import {
  Alert,
  Button,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  Snackbar,
  TextField,
  Typography,
} from "@mui/material";
import { useState, useEffect } from "react";

import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { format } from "date-fns";
import { FileText, Upload, X } from "lucide-react";
import MultiSelect from "../../../components/common/MultiSelect";
import Select from "../../../components/common/Select";
import { RequiredAsterisk } from "../../../components/professional/RequiredAsterisk";
import {
  convertStringToOptions,
  formatDateToYMD,
  formatISOToDate,
} from "../../../utils/helpers";
import { MAX_FILESIZE_MB, SIZE_KB } from "../../../constants";

const createInitialLicense = (license = {}) => {
  let initialLicenseStatus = license?.license_status || "";
  if (license?.expiry_date && initialLicenseStatus !== "expired") {
    const expiryDate = new Date(license.expiry_date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (expiryDate < today) {
      initialLicenseStatus = "expired";
    }
  }

  return {
    id: license?.id || null,
    license_upload: license?.license_upload || null,
    license_type: license?.license_type || "",
    other_license_type: license?.other_license_type || "",
    issue_authority: license?.issue_authority || "",
    license_number: license?.license_number || "",
    issue_date: license?.issue_date || null,
    license_rating: license?.license_rating || "",
    expiry_date: license?.expiry_date || null,
    license_status: initialLicenseStatus,
    renewal_date: license?.renewal_date || null,
  };
};

const LicenseForm = ({
  license = {},
  onSubmit,
  onCancel,
  certificateUrl = "",
  errors,
  setErrors,
  dropDownOptions
}) => {
  const [formData, setFormData] = useState(createInitialLicense(license));

  useEffect(() => {
    setFormData(createInitialLicense(license));
    setErrors({});
  }, [license]);

  const [fileError, setFileError] = useState("");

  const handleChange = (field, value) => {
    let newValue = value;

    // Convert date objects to YYYY-MM-DD
    if (value instanceof Date && !isNaN(value)) {
      newValue = format(value, "yyyy-MM-dd");
    }

    setFormData((prev) => {
      let finalStatus = prev.license_status;
      if ((field === "expiry_date" || field === "issue_date") && newValue) {
        const dateToCheck = field === "expiry_date" ? newValue : prev.expiry_date;
        if (dateToCheck) {
          const expiryDateObj = new Date(dateToCheck);
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          if (expiryDateObj < today) {
            finalStatus = "expired";
          } else if (finalStatus === "expired" || !finalStatus) {
            finalStatus = "active";
          }
        }
      }
      return {
        ...prev,
        [field]: newValue,
        ...((field === "expiry_date" || field === "issue_date") && { license_status: finalStatus })
      };
    });

    if (errors[field]) {
      const updatedErrors = { ...errors };
      delete updatedErrors[field];
      setErrors(updatedErrors);
    }
  };

  const validateForm = () => {
    let valid = true;
    let newErrors = {};

    if (!formData.license_type?.trim()) {
      newErrors.license_type = "License type is required";
      valid = false;
    } else if (formData.license_type === "other" && !formData.other_license_type?.trim()) {
      newErrors.other_license_type = "Please specify license type";
      valid = false;
    }
    if (!formData.issue_authority) {
      newErrors.issue_authority = "Issue authority is required";
      valid = false;
    }
    if (!formData.license_number?.trim()) {
      newErrors.license_number = "License number is required";
      valid = false;
    }
    if (!formData.license_status?.trim()) {
      newErrors.license_status = "License status is required";
      valid = false;
    }
    if (!formData.issue_date?.trim()) {
      newErrors.issue_date = "Issue date is required";
      valid = false;
    }
    if (!formData.expiry_date?.trim()) {
      newErrors.expiry_date = "Expiry date is required";
      valid = false;
    }
    if (formData.issue_date?.trim() >= formData.expiry_date?.trim()) {
      newErrors.issue_date =
        "Issue date cannot be greater or equal to expiry date";
    }

    if (
      new Date().toLocaleDateString() >
      new Date(formData.expiry_date).toLocaleDateString()
    ) {
      newErrors.expiry_date = "License is already expired";
    }
    if (!formData.license_rating?.trim()) {
      newErrors.license_rating = "At least one rating is required";
      valid = false;
    }

    if (!formData.license_upload) {
      newErrors.license_upload = "License upload is required";
      valid = false;
    }

    setErrors(newErrors);
    return valid;
  };

  const handleRemoveFile = (e) => {
    e.stopPropagation();
    setFormData((prev) => ({ ...prev, license_upload: null }));
  };

  const handleFileUpload = (file) => {
    setFileError("");

    if (!file) return;

    // Check file type
    const validTypes = ["image/jpeg", "image/png", "application/pdf"];
    if (!validTypes.includes(file.type)) {
      setFileError("Please select a JPG, PNG, or PDF file");
      return false;
    }

    // Check file size (10MB)
    if (file.size > MAX_FILESIZE_MB) {
      setFileError(`File size must be less than ${SIZE_KB} MB`);
      return false;
    }

    setFormData((prev) => ({ ...prev, license_upload: file }));

    return true;
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file && errors["license_upload"]) {
      const updatedErrors = { ...errors };
      delete updatedErrors["license_upload"];
      setErrors(updatedErrors);
    }
    handleFileUpload(file);
  };

  const handleCloseError = () => {
    setFileError("");
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      const payload = {
        ...formData,
        license_type:
          formData.license_type === "other"
            ? (formData.other_license_type || "other")
            : formData.license_type,
      };
      onSubmit(payload);
    }
  };

  return (
    <>
      <form onSubmit={handleSubmit} className="space-y-4 my-3 sentry-mask">
        {/* Aviation Category */}
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Select
                error={!!errors.license_type}
                helperText={errors.license_type}
                label={
                  <span>
                    License Type <RequiredAsterisk />
                  </span>
                }
                onChange={(value) => handleChange("license_type", value)}
                options={dropDownOptions.licenseTypeChoices}
                value={formData.license_type}
              />
            </Grid>

            {formData.license_type === "other" && (
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  id="other_license_type"
                  label={
                    <span>
                      Specify License Type <RequiredAsterisk />
                    </span>
                  }
                  value={formData.other_license_type}
                  onChange={(e) =>
                    handleChange("other_license_type", e.target.value)
                  }
                  error={!!errors.other_license_type}
                  helperText={errors.other_license_type}
                  fullWidth
                />
              </Grid>
            )}

            {/* <Grid size={{ xs: 12, sm: 6 }}>
              <Select
                error={!!errors.issue_authority}
                helperText={errors.issue_authority}
                label={
                  <span>
                    Issue Authority <RequiredAsterisk />
                  </span>
                }
                onChange={(value) => handleChange("issue_authority", value)}
                options={dropDownOptions.issuingAuthorityChoices}
                value={formData.issue_authority}
              />
            </Grid> */}

            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                id="license_number"
                // placeholder="E.g., "
                value={formData.license_number}
                onChange={(e) => handleChange("license_number", e.target.value)}
                label={
                  <span>
                    License Number <RequiredAsterisk />
                  </span>
                }
                error={!!errors.license_number}
                helperText={errors.license_number}
                fullWidth
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth error={!!errors[`issue_date`]}>
                <DatePicker
                  label={
                    <span>
                      Issue Date <RequiredAsterisk />
                    </span>
                  }
                  value={formatISOToDate(formData.issue_date)}
                  onChange={(date) =>
                    handleChange("issue_date", formatDateToYMD(date))
                  }
                  slotProps={{
                    popper: {
                      placement: "auto",
                      modifiers: [
                        {
                          name: "flip",
                          enabled: true,
                        },
                        {
                          name: "preventOverflow",
                          enabled: true,
                          options: {
                            boundary: "viewport",
                          },
                        },
                      ],
                    },
                    textField: {
                      fullWidth: true,
                      error: !!errors[`issue_date`],
                      helperText: errors[`issue_date`],
                    },
                  }}
                />
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth error={!!errors[`expiry_date`]}>
                <DatePicker
                  label={
                    <span>
                      Expiry Date <RequiredAsterisk />
                    </span>
                  }
                  value={formatISOToDate(formData.expiry_date)}
                  onChange={(date) => {
                    handleChange("expiry_date", formatDateToYMD(date));
                    if (date) {
                      const today = new Date();
                      today.setHours(0, 0, 0, 0);
                      if (date < today) {
                        handleChange("license_status", "expired");
                      }
                    }
                  }}
                  slotProps={{
                    popper: {
                      placement: "auto",
                      modifiers: [
                        {
                          name: "flip",
                          enabled: true,
                        },
                        {
                          name: "preventOverflow",
                          enabled: true,
                          options: {
                            boundary: "viewport",
                          },
                        },
                      ],
                    },
                    textField: {
                      fullWidth: true,
                      error: !!errors[`expiry_date`],
                      helperText: errors[`expiry_date`],
                    },
                  }}
                />
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: formData.license_status === "expired" ? 6 : 12 }}>
              <Select
                error={!!errors.license_status}
                helperText={errors.license_status}
                label={
                  <span>
                    License Status <RequiredAsterisk />
                  </span>
                }
                onChange={(value) => handleChange("license_status", value)}
                options={dropDownOptions.licenseStatusChoices}
                value={formData.license_status}
              />
            </Grid>

            {formData.license_status === "expired" && (
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormControl fullWidth error={!!errors[`renewal_date`]}>
                  <DatePicker
                    label="Renewal Date"
                    value={formatISOToDate(formData.renewal_date)}
                    onChange={(date) =>
                      handleChange("renewal_date", formatDateToYMD(date))
                    }
                    slotProps={{
                      popper: {
                        placement: "auto",
                        modifiers: [
                          {
                            name: "flip",
                            enabled: true,
                          },
                          {
                            name: "preventOverflow",
                            enabled: true,
                            options: {
                              boundary: "viewport",
                            },
                          },
                        ],
                      },
                      textField: {
                        fullWidth: true,
                        error: !!errors[`renewal_date`],
                        helperText: errors[`renewal_date`],
                      },
                    }}
                  />
                </FormControl>
              </Grid>
            )}

            {/* Transfer List for License Ratings */}
            {/* <Grid size={{ xs: 12, sm: 6 }}>
              <MultiSelect
                id="license_rating"
                label={
                  <span>
                    License Rating <RequiredAsterisk />
                  </span>
                }
                options={dropDownOptions.ratingChoices}
                value={convertStringToOptions(
                  formData.license_rating,
                  dropDownOptions.ratingChoices
                )}
                onChange={(value) => handleChange("license_rating", value)}
                limitTags={3}
                error={!!errors.license_rating}
                helperText={errors.license_rating}
              />
            </Grid> */}

            <Grid size={{ xs: 12 }}>
              <InputLabel htmlFor="license-upload" sx={{ fontWeight: 600 }}>
                License Upload&nbsp;
                <RequiredAsterisk />
              </InputLabel>
              <div
                className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center cursor-pointer hover:border-blue-400 transition-colors duration-200"
                onClick={() =>
                  document.getElementById(`license-upload`)?.click()
                }
              >
                <input
                  type="file"
                  id={`license-upload`}
                  className="hidden"
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={handleFileChange}
                />

                {formData.license_upload ? (
                  <div className="flex items-center justify-between bg-blue-50 rounded-md p-3">
                    <div className="flex items-center">
                      <FileText className="text-blue-500 mr-2" size={20} />
                      <Typography
                        component="a"
                        href={
                          typeof formData.license_upload === "string"
                            ? `${certificateUrl}${formData.license_upload}`
                            : URL.createObjectURL(formData.license_upload) // preview for new upload
                        }
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-700 font-medium hover:underline"
                      >
                        {typeof formData.license_upload === "string"
                          ? formData.license_upload.split("/").pop()
                          : formData.license_upload.name || "Not named"}
                      </Typography>
                    </div>
                    <IconButton
                      onClick={handleRemoveFile}
                      size="small"
                      className="!text-red-500"
                    >
                      <X size={16} />
                    </IconButton>
                  </div>
                ) : (
                  <>
                    <Upload className="mx-auto text-gray-400 mb-2" size={24} />
                    <p className="text-gray-500">Upload Certificate</p>
                    <p className="text-xs text-gray-500 mt-1">
                      PDF, JPG, PNG up to 5MB
                    </p>
                  </>
                )}
              </div>
              {errors[`license_upload`] && (
                <Typography variant="caption" color="error">
                  {errors[`license_upload`]}
                </Typography>
              )}
            </Grid>
          </Grid>
          <div className="flex justify-end space-x-2 mt-4">
            <Button onClick={onCancel}>Cancel</Button>
            <Button type="submit" variant="contained" color="primary">
              Save
            </Button>
          </div>
        </LocalizationProvider>
      </form>
      <Snackbar
        open={!!fileError}
        autoHideDuration={6000}
        onClose={handleCloseError}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseError}
          severity="error"
          className="flex items-center"
        >
          {fileError}
        </Alert>
      </Snackbar>
    </>
  );
};

export default LicenseForm;
